/**
 * The WS-2 job form (fieldiq-api#1282): a full page for New job, an inline
 * panel on the job's page for Edit. Never a modal — a job is a record.
 *
 * THE WELL DECIDES THE CUSTOMER. The form picks a well and never sends
 * customer_id; the API takes the well's customer, so well_customer_mismatch
 * cannot arise from here. The customer is shown read-only beside the pick.
 *
 * At least one scope is required (the API refuses an empty list). The rig is
 * optional on a draft, and a sheet cannot be opened until there is one. On
 * edit only the changed keys are sent; a blank text is sent as null. Status is
 * not edited here: a new job starts as Draft or Scheduled and moves by the
 * status actions afterwards.
 */
import React, { useMemo, useState } from 'react';
import { useWsRigs, useWsWells } from '../../hooks/useWellServicing';
import { blankToNull, wellIdOf } from '../../lib/well-servicing';
import type { WsCatalog, WsJob, WsJobWrite, WsMeta } from '../../types/well-servicing';
import { WS_DATE_INPUT, WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsField, WsFormError } from './WsBits';

export interface JobFormState {
  well_id: string;
  rig_id: string;
  scopes: string[];
  ticket_mode: string;
  supervisor_id: string;
  planned_start_on: string;
  planned_end_on: string;
  description: string;
  po_number: string;
  afe_number: string;
  cost_code: string;
  notes: string;
  status: string;
}

export const jobFormFrom = (j: WsJob | null, defaults: { wellId?: string } = {}): JobFormState => ({
  well_id: j?.wellId ?? defaults.wellId ?? '',
  rig_id: j?.rigId ?? '',
  scopes: j?.scopes ?? [],
  ticket_mode: j?.ticketMode ?? '',
  supervisor_id: j?.supervisorId ?? '',
  planned_start_on: j?.plannedStartOn ?? '',
  planned_end_on: j?.plannedEndOn ?? '',
  description: j?.description ?? '',
  po_number: j?.poNumber ?? '',
  afe_number: j?.afeNumber ?? '',
  cost_code: j?.costCode ?? '',
  notes: j?.notes ?? '',
  status: 'draft',
});

const TEXT_KEYS = ['supervisor_id', 'rig_id', 'planned_start_on', 'planned_end_on', 'description', 'po_number', 'afe_number', 'cost_code', 'notes'] as const;

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/** The write body: every field on create, the changed ones on edit. Exported for the test. */
export function jobWriteBody(form: JobFormState, original: JobFormState | null): WsJobWrite {
  const out: Record<string, unknown> = {};
  const changed = (k: keyof JobFormState) => !original || form[k] !== original[k];
  if (changed('well_id')) out.well_id = form.well_id;
  if (!original || !sameList(form.scopes, original.scopes)) out.scopes = form.scopes;
  if (changed('ticket_mode') && form.ticket_mode) out.ticket_mode = form.ticket_mode;
  for (const k of TEXT_KEYS)
    if (changed(k)) {
      const v = blankToNull(form[k]);
      if (original || v !== null) out[k] = v;
    }
  if (!original) out.status = form.status;
  return out as WsJobWrite;
}

export function JobForm({
  job,
  initialWellId,
  catalog,
  meta,
  users,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  job: WsJob | null;
  initialWellId?: string;
  catalog: WsCatalog | null;
  meta: WsMeta | null;
  users: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: WsJobWrite) => void;
}) {
  const original = useMemo(() => (job ? jobFormFrom(job) : null), [job]);
  const [form, setForm] = useState<JobFormState>(() => jobFormFrom(job, { wellId: initialWellId }));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = (k: keyof JobFormState, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const toggleScope = (s: string) =>
    setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));
  const wells = useWsWells();
  const rigs = useWsRigs();
  const wellList = wells.data?.data ?? [];
  const rigList = rigs.data?.data ?? [];
  const tenant = wells.data?.meta ?? meta;
  const picked = wellList.find((w) => w.id === form.well_id) ?? null;
  const scopes = catalog?.jobScopes ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.well_id) return setLocalError('Choose the well.');
    if (form.scopes.length === 0) return setLocalError('Pick at least one scope of work.');
    if (form.planned_start_on && form.planned_end_on && form.planned_end_on < form.planned_start_on)
      return setLocalError('The planned end cannot be before the start.');
    const body = jobWriteBody(form, original);
    if (original && Object.keys(body).length === 0) return onCancel();
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="ws-job-form">
      <WsFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <WsField label="Well" hint={picked ? `Customer: ${picked.customerName ?? '—'}` : 'The customer is the well’s'}>
          <select className={WS_INPUT} aria-label="Well" value={form.well_id} onChange={(e) => set('well_id', e.target.value)}>
            <option value="">{wells.isLoading ? 'Loading…' : 'Choose…'}</option>
            {wellList.map((w) => (
              <option key={w.id} value={w.id}>
                {w.wellName}
                {wellIdOf(w, tenant) ? ` · ${wellIdOf(w, tenant)}` : ''}
                {w.customerName ? ` · ${w.customerName}` : ''}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Rig" hint="Needed before a tour sheet can be opened">
          <select className={WS_INPUT} aria-label="Rig" value={form.rig_id} onChange={(e) => set('rig_id', e.target.value)}>
            <option value="">{rigs.isLoading ? 'Loading…' : 'Not yet'}</option>
            {rigList.map((r) => (
              <option key={r.id} value={r.id}>
                {r.rigNumber}
                {r.equipmentName ? ` · ${r.equipmentName}` : ''}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Tickets">
          <select className={WS_INPUT} aria-label="Tickets" value={form.ticket_mode} onChange={(e) => set('ticket_mode', e.target.value)}>
            <option value="">Customer default</option>
            {(catalog?.ticketModes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </WsField>
        <WsField label="Supervisor">
          <select className={WS_INPUT} aria-label="Supervisor" value={form.supervisor_id} onChange={(e) => set('supervisor_id', e.target.value)}>
            <option value="">None</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </WsField>
        {!job && (
          <WsField label="Start as">
            <select className={WS_INPUT} aria-label="Start as" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </WsField>
        )}
        <WsField label="Planned start">
          <input type="date" className={WS_DATE_INPUT} aria-label="Planned start" value={form.planned_start_on} onChange={(e) => set('planned_start_on', e.target.value)} />
        </WsField>
        <WsField label="Planned end">
          <input type="date" className={WS_DATE_INPUT} aria-label="Planned end" value={form.planned_end_on} onChange={(e) => set('planned_end_on', e.target.value)} />
        </WsField>
        <WsField label="PO number">
          <input className={WS_INPUT} aria-label="PO number" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} />
        </WsField>
        <WsField label="AFE number">
          <input className={WS_INPUT} aria-label="AFE number" value={form.afe_number} onChange={(e) => set('afe_number', e.target.value)} />
        </WsField>
        <WsField label="Cost code">
          <input className={WS_INPUT} aria-label="Cost code" value={form.cost_code} onChange={(e) => set('cost_code', e.target.value)} />
        </WsField>
      </div>
      <fieldset>
        <legend className="mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Scope of work</legend>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Scope of work">
          {scopes.length === 0 && <span className="text-sm text-fieldiq-muted">—</span>}
          {scopes.map((s) => (
            <label
              key={s.value}
              className={`inline-flex cursor-pointer items-center gap-2 rounded-fi-pill border px-3 py-1.5 text-sm ${
                form.scopes.includes(s.value) ? 'border-fi-amber bg-fi-amber/15 text-white' : 'border-fieldiq-border text-fieldiq-text'
              }`}
            >
              <input type="checkbox" className="accent-amber-500" checked={form.scopes.includes(s.value)} onChange={() => toggleScope(s.value)} />
              {s.label}
            </label>
          ))}
        </div>
      </fieldset>
      <WsField label="Description">
        <textarea className={`${WS_INPUT} h-20 py-2`} aria-label="Description" value={form.description} onChange={(e) => set('description', e.target.value)} />
      </WsField>
      <WsField label="Notes">
        <textarea className={`${WS_INPUT} h-20 py-2`} aria-label="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </WsField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WS_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WS_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
